import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polyline,
  Polygon,
  InfoWindow
} from '@react-google-maps/api';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  MyLocation,
  ZoomIn,
  ZoomOut,
  Layers,
  PrecisionManufacturing,
  Battery1Bar,
  Battery2Bar,
  Battery3Bar,
  BatteryFull,
  BatteryAlert,
  DeleteSweep,
  VisibilityOff
} from '@mui/icons-material';

const mapContainerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '16px'
};

const defaultCenter = {
  lat: 48.8566,
  lng: 2.3522
};

// Options par défaut pour la carte
const defaultOptions = {
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  gestureHandling: 'cooperative',
  styles: [
    {
      featureType: 'all',
      elementType: 'geometry',
      stylers: [{ color: '#242f3e' }]
    },
    {
      featureType: 'all',
      elementType: 'labels.text.stroke',
      stylers: [{ color: '#242f3e' }]
    },
    {
      featureType: 'all',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#746855' }]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#17263c' }]
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ color: '#38414e' }]
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [{ color: '#2c3e50' }]
    }
  ]
};

// Couleurs pour les zones
const zoneColors = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
  '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43'
];

// Librairies Google Maps (statique pour éviter les reloads)
const libraries = ['geometry', 'marker'];

// Composant wrapper sécurisé pour Polyline
const SafePolyline = ({ path, options, onLoad, onUnmount, ...polylineProps }) => {
  if (!path || !Array.isArray(path) || path.length < 2) {
    return null;
  }
  
  // Vérifier que tous les points sont valides
  const allValid = path.every(point => 
    point && 
    typeof point.lat === 'number' && 
    typeof point.lng === 'number' &&
    !isNaN(point.lat) && 
    !isNaN(point.lng)
  );
  
  if (!allValid) {
    return null;
  }
  
  return (
    <Polyline 
      path={path} 
      options={options} 
      onLoad={onLoad}
      onUnmount={onUnmount}
      {...polylineProps} 
    />
  );
};

// Composant wrapper pour les marqueurs (compatible avec les futures migrations)
const SafeMarker = ({ position, onClick, icon, title, animation, children, ...props }) => {
  // Utiliser Marker classique avec suppression automatique des warnings AdvancedMarkerElement
  return (
    <Marker
      position={position}
      onClick={onClick}
      icon={icon}
      title={title}
      animation={animation}
      {...props}
    >
      {children}
    </Marker>
  );
};

function AutomowerMap({ 
  currentPosition, 
  historyPoints = [], 
  zones = [], 
  onMarkerClick,
  showTrail = true,
  showZones = true,
  mapCenter,
  zoom = 20,
  selectedDate = null
}) {
  // S'assurer que zones est toujours un tableau
  const safeZones = Array.isArray(zones) ? zones : [];
  
  
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [overlayKey, setOverlayKey] = useState(0);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapType, setMapType] = useState('satellite');
  const [polylineKey, setPolylineKey] = useState(Date.now());
  const [hidePolylines, setHidePolylines] = useState(false);
  const [mapKey, setMapKey] = useState(Date.now()); // Clé pour forcer la recréation complète de la carte
  const polylineInstanceRef = useRef(null);
  const lastSelectedDateRef = useRef(selectedDate);

  // Fonction pour nettoyer la polyline existante
  const clearPolyline = useCallback(() => {
    if (polylineInstanceRef.current && polylineInstanceRef.current.setMap) {
      polylineInstanceRef.current.setMap(null);
      polylineInstanceRef.current = null;
    }
  }, []);

  // Liste de TOUTES les polylines créées pour pouvoir les supprimer
  const allPolylinesRef = useRef([]);

  // Fonction pour nettoyer TOUTES les polylines de la carte
  const clearAllMapPolylines = useCallback(() => {
    // Supprimer toutes les polylines qu'on a tracées
    allPolylinesRef.current.forEach((polyline) => {
      if (polyline && polyline.setMap) {
        polyline.setMap(null);
      }
    });
    allPolylinesRef.current = [];
    
    // Reset référence courante aussi
    if (polylineInstanceRef.current) {
      polylineInstanceRef.current = null;
    }
  }, []);

  // Fonction de test pour forcer le nettoyage manuel ET empêcher recréation
  const testClearPolylines = useCallback(() => {
    // Cacher temporairement les polylines
    setHidePolylines(true);
    
    // Utiliser la nouvelle méthode qui supprime TOUT
    clearAllMapPolylines();
    
    // Attendre un peu avant de permettre recréation
    setTimeout(() => {
      setHidePolylines(false);
      setPolylineKey(Date.now() + Math.random());
    }, 500);
  }, [clearAllMapPolylines]);

  // Fonction pour masquer/afficher complètement les polylines
  const togglePolylines = useCallback(() => {
    const newHideState = !hidePolylines;
    setHidePolylines(newHideState);
    
    if (newHideState) {
      // Si on cache, supprimer aussi les polylines existantes
      clearAllMapPolylines();
    }
  }, [hidePolylines, clearAllMapPolylines]);

  // Forcer la réinitialisation du tracé quand la date change
  useEffect(() => {
    if (lastSelectedDateRef.current !== selectedDate) {
      lastSelectedDateRef.current = selectedDate;

      // MÉTHODE RADICALE: Recréer complètement la carte pour éliminer toutes les polylines
      allPolylinesRef.current = [];
      polylineInstanceRef.current = null;

      // Forcer la recréation complète du composant GoogleMap
      setMapKey(Date.now());
      setPolylineKey(Date.now());
      // Le nouveau onLoad remettra mapReady à true
      setMapReady(false);
    }
  }, [selectedDate]);

  // [DÉSACTIVÉ] Gérer les changements de points 
  // PROBLÈME: Ces effets se déclenchent plusieurs fois lors du changement de date
  // et créent des polylines multiples. On laisse [EFFECT 1] gérer tout via la 
  // recréation complète de la carte.

  // Nettoyer toutes les polylines lors du unmount du composant
  useEffect(() => {
    return () => {
      clearAllMapPolylines();
    };
  }, [clearAllMapPolylines]);

  // Charger l'API Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
    // Options additionnelles pour gérer les erreurs
    onError: (error) => {
      console.error('Erreur chargement Google Maps:', error);
    }
  });

  // Supprimer les warnings de dépréciation Google Maps
  useEffect(() => {
    const originalWarn = console.warn;
    
    const shouldSuppressMessage = (message) => {
      if (typeof message !== 'string') return false;
      
      return (
        message.includes('google.maps.Marker is deprecated') ||
        message.includes('AdvancedMarkerElement') ||
        message.includes('Marker is deprecated') ||
        message.includes('use AdvancedMarkerElement') ||
        message.includes('The Marker class is deprecated') ||
        message.includes('Please use the AdvancedMarkerElement') ||
        message.toLowerCase().includes('advancedmarkerelement')
      );
    };
    
    console.warn = (...args) => {
      const message = args[0];
      if (!shouldSuppressMessage(message)) {
        originalWarn.apply(console, args);
      }
    };
    
    return () => {
      console.warn = originalWarn;
    };
  }, []);

  const onLoad = useCallback((map) => {
    setMap(map);
    // FIX: les overlays (polyline, markers, polygons) qui ont été montés AVANT
    // que GoogleMap soit prêt restent invisibles silencieusement. On force un
    // remount complet de tous les overlays via une clé qui change après le
    // chargement de la carte.
    setTimeout(() => {
      allPolylinesRef.current.forEach(p => p && p.setMap && p.setMap(null));
      allPolylinesRef.current = [];
      polylineInstanceRef.current = null;
      setPolylineKey(Date.now() + Math.random());
      setOverlayKey(k => k + 1);
      setMapReady(true);
    }, 50);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
    setMapReady(false);
  }, []);

  // Centrer la carte sur la position actuelle ou calculer les bounds
  useEffect(() => {
    if (map && currentPosition && currentPosition.lat !== undefined && currentPosition.lng !== undefined) {
      // Validation stricte des coordonnées
      const lat = Number(currentPosition.lat);
      const lng = Number(currentPosition.lng);
      
      if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) &&
          lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        
        const center = mapCenter || { lat, lng };
        
        try {
          map.panTo(center);
        } catch (error) {
          console.error('Erreur panTo:', error);
        }
      }
    } else if (map && historyPoints && historyPoints.length > 0) {
      // Calculer les bounds pour inclure tous les points
      const bounds = new window.google.maps.LatLngBounds();
      let validPointsCount = 0;
      
      historyPoints
        .filter(point => {
          if (!point) return false;
          const lat = Number(point.lat);
          const lng = Number(point.lng);
          return !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) &&
                 lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
        })
        .forEach(point => {
          const lat = Number(point.lat);
          const lng = Number(point.lng);
          bounds.extend({ lat, lng });
          validPointsCount++;
        });
      
      if (validPointsCount > 0) {
        try {
          map.fitBounds(bounds);
        } catch (error) {
          console.error('Erreur fitBounds:', error);
        }
      }
    }
  }, [map, currentPosition, historyPoints, mapCenter]);

  // Icône de batterie basée sur le niveau
  const getBatteryIcon = (batteryLevel) => {
    if (batteryLevel === null || batteryLevel === undefined) return <BatteryAlert />;
    if (batteryLevel <= 20) return <BatteryAlert sx={{ color: '#ef4444' }} />;
    if (batteryLevel <= 40) return <Battery1Bar sx={{ color: '#f59e0b' }} />;
    if (batteryLevel <= 60) return <Battery2Bar sx={{ color: '#eab308' }} />;
    if (batteryLevel <= 80) return <Battery3Bar sx={{ color: '#84cc16' }} />;
    return <BatteryFull sx={{ color: '#22c55e' }} />;
  };

  // Couleur du marqueur selon le statut
  const getMarkerColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'mowing': return '#22c55e';
      case 'going_home': return '#f59e0b';
      case 'charging': return '#3b82f6';
      case 'leaving': return '#8b5cf6';
      case 'parked_in_cs': return '#6b7280';
      case 'stopped_in_garden': return '#ef4444';
      case 'error': return '#dc2626';
      case 'fatal_error': return '#991b1b';
      case 'error_at_power_up': return '#dc2626';
      default: return '#6b7280';
    }
  };

  // Créer l'icône personnalisée du robot tondeuse
  const createRobotIcon = (status, batteryLevel, isCurrentPosition = false) => {
    const color = getMarkerColor(status);
    
    if (isCurrentPosition) {
      // Marqueur rouge plus gros et très visible pour la position actuelle
      return {
        path: 0, // CIRCLE
        fillColor: '#dc2626', // Rouge foncé
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
        scale: 6 // Taille optimale
      };
    } else {
      // Icône plus petite pour l'historique
      return {
        path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
        fillColor: color,
        fillOpacity: 0.6,
        strokeColor: '#ffffff',
        strokeWeight: 1,
        scale: 4
      };
    }
  };

  // Gestionnaires de contrôles
  const handleZoomIn = () => {
    if (map) map.setZoom(map.getZoom() + 1);
  };

  const handleZoomOut = () => {
    if (map) map.setZoom(map.getZoom() - 1);
  };

  const handleMyLocation = () => {
    if (map && currentPosition) {
      map.panTo({ lat: currentPosition.lat, lng: currentPosition.lng });
      map.setZoom(20);
    }
  };

  const toggleMapType = () => {
    const newType = mapType === 'satellite' ? 'roadmap' : 'satellite';
    setMapType(newType);
    if (map) map.setMapTypeId(newType);
  };

  if (loadError) {
    return (
      <Alert severity="error" sx={{ borderRadius: '16px' }}>
        <Typography variant="h6" gutterBottom>Erreur Google Maps</Typography>
        <Typography variant="body2" gutterBottom>
          Impossible de charger Google Maps. Vérifiez :
        </Typography>
        <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
          <li>La clé API Google Maps est valide</li>
          <li>L'API JavaScript Maps est activée</li>
          <li>Les restrictions de domaine sont configurées</li>
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          Clé API: {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? 
            `${process.env.REACT_APP_GOOGLE_MAPS_API_KEY.substring(0, 10)}...` : 
            'Non définie'}
        </Typography>
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={500}
        sx={{ 
          borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">Chargement de la carte...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box position="relative">
      <GoogleMap
        key={mapKey} // Clé pour forcer recréation complète
        mapContainerStyle={mapContainerStyle}
        center={(() => {
          // Validation du centre de la carte
          if (mapCenter && 
              typeof mapCenter.lat === 'number' && 
              typeof mapCenter.lng === 'number' &&
              !isNaN(mapCenter.lat) && !isNaN(mapCenter.lng)) {
            return mapCenter;
          }
          
          if (currentPosition && 
              typeof currentPosition.lat === 'number' && 
              typeof currentPosition.lng === 'number' &&
              !isNaN(currentPosition.lat) && !isNaN(currentPosition.lng)) {
            return { lat: currentPosition.lat, lng: currentPosition.lng };
          }
          
          // Éviter les logs répétitifs du centre par défaut
          return defaultCenter;
        })()}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          ...defaultOptions,
          mapTypeId: mapType
        }}
      >
        {/* Zones AIM */}
        {showZones && safeZones.map((zone, index) => (
          zone.polygon && zone.polygon.length > 0 && (
            <Polygon
              key={`${zone.id}-${overlayKey}`}
              paths={zone.polygon.map(point => ({
                lat: point.latitude || point.lat,
                lng: point.longitude || point.lng
              }))}
              options={{
                fillColor: zone.color || zoneColors[index % zoneColors.length],
                fillOpacity: 0.2,
                strokeColor: zone.color || zoneColors[index % zoneColors.length],
                strokeOpacity: 0.8,
                strokeWeight: 2
              }}
              onClick={() => {
                setSelectedMarker({
                  type: 'zone',
                  data: zone,
                  position: zone.center || zone.polygon[0]
                });
              }}
            />
          )
        ))}

        {/* Tracé de l'historique */}
        {showTrail && historyPoints && Array.isArray(historyPoints) && historyPoints.length > 1 && (() => {
          try {
            const validPoints = historyPoints
              .filter(point => {
                if (!point || typeof point !== 'object') return false;
                const lat = Number(point.lat);
                const lng = Number(point.lng);
                const isValid = !isNaN(lat) && 
                               !isNaN(lng) &&
                               lat >= -90 && lat <= 90 &&
                               lng >= -180 && lng <= 180 &&
                               lat !== 0 && lng !== 0; // Éviter les coordonnées (0,0)
                return isValid;
              })
              .map(point => ({
                lat: Number(point.lat),
                lng: Number(point.lng)
              }));
            
            
            // Besoin d'au moins 2 points et Google Maps doit être chargé
            if (validPoints.length < 2 || !window.google || !window.google.maps) {
              return null;
            }
            
            // Créer un dégradé de couleurs selon l'âge des points
            const gradient = [
              '#22c55e', // Vert pour les points récents (tonte)
              '#3b82f6', // Bleu pour les points moyens  
              '#667eea', // Violet pour les points anciens
              '#94a3b8'  // Gris pour les très anciens
            ];
            
            // Créer une clé unique basée sur les coordonnées pour forcer la recréation
            const coordsHash = validPoints.length > 0 ? 
              `${validPoints[0].lat}_${validPoints[0].lng}_${validPoints[validPoints.length-1].lat}_${validPoints[validPoints.length-1].lng}` : 
              'empty';
            
            return (
              <SafePolyline
                key={`polyline-${polylineKey}-${validPoints.length}-${coordsHash}-${selectedDate}`}
                path={validPoints}
                onLoad={(polyline) => {
                  // Ajouter à la liste de toutes les polylines
                  allPolylinesRef.current.push(polyline);
                  // Garder aussi la référence courante
                  polylineInstanceRef.current = polyline;
                }}
                onUnmount={(polyline) => {
                  if (polyline && polyline.setMap) {
                    polyline.setMap(null);
                  }
                  // Retirer de la liste de toutes les polylines
                  allPolylinesRef.current = allPolylinesRef.current.filter(p => p !== polyline);
                  // Reset référence courante si c'est celle-ci
                  if (polylineInstanceRef.current === polyline) {
                    polylineInstanceRef.current = null;
                  }
                }}
                options={{
                  geodesic: true,
                  strokeColor: '#22c55e', // Vert principal pour la tonte
                  strokeOpacity: 0.9,
                  strokeWeight: 4,
                  // Effet de lueur
                  icons: [{
                    icon: {
                      path: 'M 0,0 0,1',
                      strokeOpacity: 0.3,
                      strokeColor: '#22c55e',
                      strokeWeight: 8
                    },
                    repeat: '10px'
                  }]
                }}
              />
            );
          } catch (error) {
            console.error('Erreur Polyline:', error);
            return null;
          }
        })()}

        {/* Position actuelle du robot */}
        {mapReady && currentPosition &&
         typeof currentPosition.lat === 'number' &&
         typeof currentPosition.lng === 'number' &&
         !isNaN(currentPosition.lat) &&
         !isNaN(currentPosition.lng) && (
          <SafeMarker
            key={`robot-${overlayKey}`}
            position={{ lat: Number(currentPosition.lat), lng: Number(currentPosition.lng) }}
            icon={createRobotIcon(currentPosition.status, currentPosition.battery, true)}
            onClick={() => {
              setSelectedMarker({
                type: 'robot',
                data: currentPosition,
                position: { lat: Number(currentPosition.lat), lng: Number(currentPosition.lng) }
              });
              if (onMarkerClick) onMarkerClick(currentPosition);
            }}
            animation={window.google?.maps?.Animation?.DROP || 2}
            title={`Robot Automower - ${currentPosition.status || 'Statut inconnu'}`}
            optimized={false}
          />
        )}

        {/* Points d'historique (optionnel, pour les positions importantes) */}
        {historyPoints && Array.isArray(historyPoints) && historyPoints
          .filter(point => {
            const isValid = point && 
                           typeof point.lat === 'number' && 
                           typeof point.lng === 'number' && 
                           !isNaN(point.lat) && 
                           !isNaN(point.lng) &&
                           point.lat >= -90 && point.lat <= 90 &&
                           point.lng >= -180 && point.lng <= 180;
            return isValid;
          })
          .filter((_, index) => index % 10 === 0) // Afficher 1 point sur 10
          .map((point, index) => mapReady && (
            <SafeMarker
              key={`history-${index}-${point.lat}-${point.lng}-${overlayKey}`}
              position={{ lat: Number(point.lat), lng: Number(point.lng) }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                fillColor: getMarkerColor(point.status),
                fillOpacity: 0.6,
                strokeColor: '#ffffff',
                strokeWeight: 1,
                scale: 3
              }}
              onClick={() => {
                setSelectedMarker({
                  type: 'history',
                  data: point,
                  position: { lat: Number(point.lat), lng: Number(point.lng) }
                });
              }}
            />
          ))}

        {/* Fenêtre d'information */}
        {selectedMarker && (
          <InfoWindow
            position={selectedMarker.position}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <Card sx={{ minWidth: 200, boxShadow: 'none' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {selectedMarker.type === 'robot' && (
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PrecisionManufacturing color="primary" />
                      <Typography variant="h6">Position actuelle</Typography>
                    </Box>
                    
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Chip 
                        label={selectedMarker.data.status || 'Inconnu'}
                        color="primary"
                        size="small"
                      />
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        {getBatteryIcon(selectedMarker.data.battery)}
                        <Typography variant="body2">
                          {selectedMarker.data.battery}%
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary">
                        Zone: {selectedMarker.data.workAreaName || 'Inconnue'}
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary">
                        Lat: {selectedMarker.data.lat?.toFixed(6)}<br />
                        Lng: {selectedMarker.data.lng?.toFixed(6)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {selectedMarker.type === 'zone' && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {selectedMarker.data.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Surface: {selectedMarker.data.totalArea ? 
                        `${Math.round(selectedMarker.data.totalArea)} m²` : 
                        'Non calculée'
                      }
                    </Typography>
                  </Box>
                )}

                {selectedMarker.type === 'history' && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Point d'historique
                    </Typography>
                    <Chip 
                      label={selectedMarker.data.status}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {selectedMarker.data.timestamp ? 
                        new Date(selectedMarker.data.timestamp).toLocaleString() :
                        'Heure inconnue'
                      }
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Contrôles personnalisés */}
      <Box
        position="absolute"
        top={16}
        right={16}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Tooltip title="Zoom +">
          <IconButton
            onClick={handleZoomIn}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' }
            }}
          >
            <ZoomIn />
          </IconButton>
        </Tooltip>

        <Tooltip title="Zoom -">
          <IconButton
            onClick={handleZoomOut}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' }
            }}
          >
            <ZoomOut />
          </IconButton>
        </Tooltip>

        <Tooltip title="Ma position">
          <IconButton
            onClick={handleMyLocation}
            disabled={!currentPosition}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' }
            }}
          >
            <MyLocation />
          </IconButton>
        </Tooltip>

        <Tooltip title="Type de carte">
          <IconButton
            onClick={toggleMapType}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' }
            }}
          >
            <Layers />
          </IconButton>
        </Tooltip>

        <Tooltip title="🧪 TEST: Nettoyer polylines">
          <IconButton
            onClick={testClearPolylines}
            sx={{
              backgroundColor: 'rgba(255, 87, 87, 0.9)',
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 87, 87, 1)' }
            }}
          >
            <DeleteSweep />
          </IconButton>
        </Tooltip>

        <Tooltip title={hidePolylines ? "Afficher les tracés" : "Masquer les tracés"}>
          <IconButton
            onClick={togglePolylines}
            sx={{
              backgroundColor: hidePolylines ? 'rgba(76, 175, 80, 0.9)' : 'rgba(158, 158, 158, 0.9)',
              color: 'white',
              '&:hover': { 
                backgroundColor: hidePolylines ? 'rgba(76, 175, 80, 1)' : 'rgba(158, 158, 158, 1)' 
              }
            }}
          >
            <VisibilityOff />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default AutomowerMap;